import React from 'react';
import { Button, Nav, NavLink } from 'reactstrap';
import IconHelper from '../common/IconHelper';
import Link from '../common/Link';
import UnreadMessages from '../messages/UnreadMessages';
import {
  toPremium,
  toDashboard,
  toProfile,
  toJobSearchResults,
  toAssessments,
  toConversationList,
  toCERequirements,
  toMyClassifieds,
  toAccountSettings,
  toJobPostings,
  toInterviews,
  toContactUs,
  toBlog,
  toEventsList,
  toClassifiedsList,
  toResources,
  toSubscription,
  toBrowseJobs,
} from '../../routes';
import {
  UserContext,
  UserContextType,
  isEmployerType,
  isStaffType,
} from '../../context/UserContext';
import { useContext } from 'react';
import { JobPostingContext } from '../../context/JobPostingContext';

const NAV_STYLE = {
  flex: '1 1 0',
  minHeight: 0,
  overflowY: 'auto' as const,
  flexWrap: 'nowrap' as const,
  width: 'fit-content',
  whiteSpace: 'nowrap' as const,
};
import { isCapacitorBuild, openInCapacitorBrowser } from '../../utils/platform';

const Sidenav = (props: { toggle?: () => void }) => {
  const { user } = useContext(UserContext) as UserContextType;
  const { subscription } = useContext(JobPostingContext);

  const navLinks = isEmployerType(user)
    ? [
        {
          route: toDashboard,
          label: 'Dashboard',
          icon: 'FaHome',
        },
        {
          route: toJobPostings,
          label: 'Job Postings',
          icon: 'FaFileAlt',
          testId: 'jobPostings',
        },
        !!subscription
          ? {
              route: toSubscription,
              label: 'Subscription',
              icon: 'FaSync',
            }
          : {},
        {
          route: toProfile,
          label: 'Employer Profile',
          icon: 'FaBuilding',
        },
        {
          route: toAssessments,
          label: 'Assessments',
          icon: 'FaChartPie',
        },
        {
          route: toInterviews,
          label: 'Interviews',
          icon: 'FaCalendarAlt',
        },
        {
          route: toConversationList,
          label: 'Messages',
          icon: 'FaComments',
        },
        {
          route: toMyClassifieds,
          label: 'My Classifieds',
          icon: 'FaNewspaper',
        },
        {
          route: toAccountSettings,
          label: 'Settings',
          icon: 'FaCog',
        },
      ]
    : [
        {
          route: toDashboard,
          label: 'Dashboard',
          icon: 'FaHome',
        },
        {
          route: toProfile,
          label: 'Resume Profile',
          testId: 'profileLink',
          icon: 'FaAddressCard',
        },
        {
          route: isStaffType(user)
            ? toJobSearchResults(user.position, user.zip)
            : toBrowseJobs,
          label: 'Search Jobs',
          testId: 'searchJobs',
          icon: 'FaSearch',
        },
        {
          route: toAssessments,
          label: 'Assessments',
          testId: 'assessmentsLink',
          icon: 'FaChartPie',
        },
        {
          route: toInterviews,
          label: 'Interviews',
          icon: 'FaCalendarAlt',
        },
        {
          route: toConversationList,
          label: 'Messages',
          icon: 'FaComments',
        },
        {
          route: toPremium,
          label: 'Premium',
          icon: 'FaStar',
        },
        {
          route: toCERequirements,
          label: "My CE's",
          icon: 'FaBook',
        },
        {
          route: toMyClassifieds,
          label: 'My Classifieds',
          icon: 'FaNewspaper',
        },
        {
          route: toAccountSettings,
          label: 'Settings',
          icon: 'FaCog',
        },
      ];

  return (
    <div className="d-flex flex-column justify-content-stretch h-100">
      <Nav
        vertical
        style={NAV_STYLE}
        className={`col-auto ${isStaffType(user) ? 'staff-nav' : 'employer-nav'}`}
      >
        {navLinks
          .filter((link) => !!link.label)
          .map((link) => (
            <NavLink
              key={link.label}
              tag={Link}
              to={link.route}
              active={!!link.route && location.pathname.includes(link.route)}
              onClick={props.toggle}
            >
              {link.icon && <IconHelper icon={link.icon} />}
              {link.label}
              {link.label === 'Messages' && <UnreadMessages />}
            </NavLink>
          ))}
        <hr className="my-4 w-75 mx-auto" />
        <NavLink tag={Link} to={toResources} onClick={props.toggle}>
          Resources
        </NavLink>
        <NavLink
          tag={Link}
          to={toBlog}
          className="dp-nav-link rounded my-1"
          onClick={!!props.toggle ? props.toggle : undefined}
        >
          Blog
        </NavLink>
        <NavLink
          tag={Link}
          to={toEventsList}
          className="dp-nav-link rounded my-1"
          onClick={!!props.toggle ? props.toggle : undefined}
        >
          Dental Events & CE's
        </NavLink>
        <NavLink
          tag={Link}
          to={toClassifiedsList()}
          className="dp-nav-link rounded my-1"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            // Same rationale as Events above — /dental-jobs/classifieds-list/
            // is not bundled in Capacitor (createClassifieds is skipped),
            // so route the click through @capacitor/browser. See the Events
            // intercept above for the Gatsby Link e.defaultPrevented contract.
            if (isCapacitorBuild) {
              e.preventDefault();
              openInCapacitorBrowser(toClassifiedsList());
            }
            if (props.toggle) props.toggle();
          }}
        >
          Browse Classifieds
        </NavLink>
        <div style={{ height: '120px' }}></div>
      </Nav>
      <div className="text-center bg-solids-off-white-50 p-3 border-top mt-auto">
        <h4>Questions?</h4>
        <Button
          color="secondary"
          tag={Link}
          to={toContactUs}
          onClick={props.toggle}
        >
          Contact Us
        </Button>
      </div>
    </div>
  );
};

export default Sidenav;
